import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { BroadcastScheduleProgramBodySegmentToolkitNavigation } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitNavigation';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useDeleteClipDeclaration } from '@/features/clips/api/delete-clips-declaration';
import { useClipStatementsPermissions } from '@/features/clips/hooks/useClipStatementsPermissions';
import { TClipDeclaration } from '@/features/clips/types';

import { ClipToolkitDeclarationsForm } from './components/ClipToolkitDeclarationsForm';
import { ClipToolkitDeclarationsTable } from './components/ClipToolkitDeclarationsTable';

export default function ClipToolkitDeclarations() {
  const {
    formBox,
    declarationClipData,
    activeClip,
    setFormBox,
    setDeclarationClipData,
    setActiveClip,
    handleTooltikClick,
    setClipItems,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: deleteClipDeclaration } = useDeleteClipDeclaration();
  const { t } = useTranslation();

  const { canCreate, canUpdate, canDelete } = useClipStatementsPermissions();

  const handleEdit = useCallback(
    (item: TClipDeclaration) => {
      if (!canUpdate) return;
      setFormBox('declaration');
      setDeclarationClipData(item);
    },
    [setFormBox, canUpdate]
  );

  const handleCreate = useCallback(() => {
    if (!canCreate) return;
    setFormBox('declaration');
    setDeclarationClipData(null);
  }, [setFormBox, setDeclarationClipData, canCreate]);

  const handleDelete = useCallback(
    (item: TClipDeclaration) => {
      if (!canDelete) return;
      deleteClipDeclaration({ id: item.id || '' }).then(() => {
        setActiveClip(prev => {
          if (prev) {
            return {
              ...prev,
              clip_declarations: prev.clip_declarations?.filter(
                declaration => declaration.id !== item.id
              ),
            };
          }
          return prev;
        });
        setClipItems(prev => {
          if (!prev) return prev;
          return prev.map(clip => {
            if (clip.id === activeClip?.id) {
              return {
                ...clip,
                clip_declarations: clip.clip_declarations?.filter(
                  declaration => declaration.id !== item.id
                ),
              };
            }
            return clip;
          });
        });
      });
    },
    [canDelete]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        handleCreate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreate]);

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-3 lg:order-2'),
        container: 'py-size-4',
      }}
    >
      <div className="flex w-full flex-col gap-2">
        {formBox !== 'declaration' ? (
          <>
            <GeneralHeader
              name={t('Declaraciones')}
              buttonName={canCreate ? t('Crear') : undefined}
              onClick={handleCreate}
              onClose={() => handleTooltikClick(ESegmentToolkit.PERSONAS)}
            />
            <ClipToolkitDeclarationsTable
              items={activeClip?.clip_declarations || []}
              onPreview={() => {}}
              onUpdate={canUpdate ? handleEdit : undefined}
              onDelete={canDelete ? handleDelete : undefined}
            />
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <ClipToolkitDeclarationsForm />
            <BroadcastScheduleProgramBodySegmentToolkitNavigation
              items={activeClip?.clip_declarations || []}
              activeItem={declarationClipData}
              onChangeData={setDeclarationClipData}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
